import { Loader, Table } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React from 'react';
import { CheckboxChangeEvent } from '../../../../components/formatters/CheckboxCellRenderer/CheckboxCellRenderer.types';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import { columnsConfig, sortIssuers } from './WatchlistTable.logic';
import useFetchFilters from '../../../../hooks/tags/useFetchFilters';
import { FilterType } from '../../../../models/Filter';
import styles from './WatchlistTable.module.scss';
import { IssuerListItemDto } from '../../../../services/dtos/issuer/issuerList.dto';

const cx = classNames.bind(styles);

interface WatchlistTableProps {
  issuers: IssuerListItemDto[] | undefined;
  isIssuersLoading: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  issuersToRemove: string[];
  onIssuerSelected: (checkboxChangeEvent: CheckboxChangeEvent) => void;
  fetchNextPage?: () => void;
}

const WatchlistTable = ({
  issuers,
  isIssuersLoading,
  isFetchingNextPage,
  hasNextPage,
  issuersToRemove,
  onIssuerSelected,
  fetchNextPage,
}: WatchlistTableProps): React.ReactElement | null => {
  const { data: tags, isLoading: isLoadingTags } = useFetchFilters([
    FilterType.region,
    FilterType.sector,
    FilterType.creditGrade,
  ]);

  const isLoading = isIssuersLoading || isLoadingTags;

  if (!issuers) {
    return null;
  }
  const sortedIssuers = sortIssuers(issuers);

  const checkboxCellProps = {
    onClick: onIssuerSelected,
    getId: (issuer) => issuer?.tagId,
    getIsChecked: (item) => (!item ? !!issuersToRemove.length : issuersToRemove.includes(item.tagId)),
    getIsIndeterminate: (item) =>
      item ? false : Boolean(issuersToRemove.length && issuersToRemove.length < sortedIssuers?.length),
  };

  const renderInfinitePagingFooter = () => {
    if ((isLoading && !isFetchingNextPage) || !hasNextPage) {
      return null;
    }

    return (
      <div className={cx('footer')}>
        <LoadMoreButton onClick={() => fetchNextPage?.()} isLoading={isFetchingNextPage} />
      </div>
    );
  };

  return (
    <div className={cx('body')}>
      {isLoading ? (
        <Loader position="center" />
      ) : (
        <>
          <Table
            data={sortedIssuers}
            tableViewConfig={columnsConfig(cx('cell'), tags, checkboxCellProps)}
            getKey={(issuer, index) => `${issuer.tagId}-${index}`}
            autoLayout
          />
          {renderInfinitePagingFooter()}
        </>
      )}
    </div>
  );
};

export default WatchlistTable;
